/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { AddSharp } from "@mui/icons-material";
import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  GetDeliveryOrderByIdApiResponse,
  GetDeliveryOrderByIdArray,
} from "../../../@types/DRO/DeliveryOrderResponse";
import { DeliveryOrderApi } from "../../../api/DeliveryOrder/DeliveryOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import DeliveryOrderTableComponent from "../../Layouts/Table/DeliveryOrderTableComponent";
import ViewDelivaryOrder from "../../UI/Views/ViewDelivaryOrder";
import { CreateDeliveryOrder } from "../CreateOrder/CreateDeliveryOrder";
import CreateDeliveryOrderDraft from "../CreateOrder/CreateDeliveryOrderDraft";
import NewDeliveryOrderForm from "../CreateOrder/NewDeliveryOrderForm";
import CustomDropdown from "../CustomDropdown/CustomDropdown";

const deliveryOrderStatusOptions = [
  { value: "", label: "All" },
  { value: "DRAFT", label: "Draft" },
  { value: "CREATED", label: "Created" },
  { value: "COMPLETED", label: "Completed" },
];

const DeliveryOrdersPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = id ? Number(id) : null;
  const [orderState, setOrderState] = useState<string | null>(null);

  const columns = [
    "ID",
    "Reference",
    "From",
    "To",
    "Creation Date",
    "Status",
    "Actions",
  ];

  const [showDeliveryOrder, setShowDeliveryOrder] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState<GetDeliveryOrderByIdArray[]>([]);
  const [viewState, setViewState] = useState<number | undefined>(undefined);
  const [newOrder, setNewOrder] = useState(false);
  const [returned, setReturned] = useState<number | null>(null);
  const { setLoading } = useLoading();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProductOrderData();
  }, [page, pageSize, search, statusFilter]);

  const fetchProductOrderData = async () => {
    setLoading(true);
    try {
      const response: GetDeliveryOrderByIdApiResponse =
        await DeliveryOrderApi.api.getDeliveryOrderApi(
          page,
          pageSize,
          search,
          undefined,
          undefined,
          statusFilter
        );
      setData(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching product order data:", error);
    }
    setLoading(false);
  };

  // Update URL when status filter changes
  useEffect(() => {
    if (statusFilter) {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, setSearchParams]);

  const handleDeliveryOrder = () => {
    setNewOrder(true);
    navigate(`/delivery-order/create${statusFilter ? `?status=${statusFilter}` : ""}`);
  };

  const handleBackToOrders = () => {
    setShowDeliveryOrder(false);
    setNewOrder(false);
    setReturned(null);
    fetchProductOrderData();
  };

  const formatData = (data: any[]) => {
    return data.map((item) => ({
      ID: item.id,
      Reference: item.referenceCode,
      From: item.vendorWarehouseDto?.vendorName,
      To: item.deliveryWareHouseDetails?.deliveryWarehouseName,
      Contact: "9876543210",
      "Creation Date": item.creationDate || "N/A",
      Total: item.total ?? "N/A",
      Status: {
        displayName: item.status?.displayName,
        name: item.status?.name,
      },
      Actions: {
        text: "View",
      },
    }));
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  useEffect(() => {
    if (orderId) {
      setShowDeliveryOrder(true);
    }
  }, [orderId]);

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="w-64 bg-white shadow-lg">
        <SideBarNavigation />
      </div>
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {returned && orderState ? (
            <CreateDeliveryOrderDraft
              onBack={handleBackToOrders}
              orderStage={orderState}
              orderDataId={returned}></CreateDeliveryOrderDraft>
          ) : viewState !== undefined ? (
            <ViewDelivaryOrder
              orderId={viewState}
              onBack={() => setViewState(undefined)}
            />
          ) : showDeliveryOrder ? (
            <CreateDeliveryOrder
              orderId={orderId}
              onBack={handleBackToOrders}
            />
          ) : newOrder ? (
            <>
              <NewDeliveryOrderForm handleBackToOrders={handleBackToOrders} />
            </>
          ) : (
            <>
              {/* Header */}
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-semibold text-gray-900">
                  Delivery Orders
                </h1>
                <Button
                  variant="contained"
                  startIcon={<AddSharp />}
                  onClick={handleDeliveryOrder}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#1f2937",
                    "&:hover": { bgcolor: "#111827" },
                    fontSize: "14px",
                    borderRadius: "32px",
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                  }}>
                  Add Order
                </Button>
              </div>

              {/* Filters and Search */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-4">
                  <CustomDropdown
                    label="Status:"
                    options={deliveryOrderStatusOptions}
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    minWidth="130px"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by order ID or reference"
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <svg
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <DeliveryOrderTableComponent
                  columns={columns}
                  data={formatData(data)}
                  viewState={viewState}
                  setViewState={setViewState}
                  returned={returned}
                  setReturned={setReturned}
                  setOrderState={setOrderState}
                  currentPage={page}
                  setCurrentPage={setPage}
                  totalPages={totalPages}
                  search={search}
                  statusFilter={statusFilter}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryOrdersPage;
